import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import Grid from '@components-clean/core/grid-fe';
import {
    getTrialOsStudyTeamColumns,
    riskIQAccessTranslationKeys,
} from '@pages-clean/admin/study/manage-trialos-study-team/trialos-study-team.data';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { GET_TRIALOS_STUDY_TEAM_MEMBERS } from '@queries/studies';

interface TrialOsStudyTeamRow {
    id: string;
    riskIQ: string;
    lastName: string;
    firstName: string;
    email: string;
    createdOn: string | null;
    createdBy: string;
    updatedOn: string | null;
    updatedBy: string;
}

const getFullName = (person: { firstName: string | null; lastName: string | null } | null) =>
    [person?.firstName, person?.lastName].filter(Boolean).join(' ');

const ManageTrialOsStudyTeam = () => {
    const { t } = useTranslation('admin', { keyPrefix: 'trialOsStudyTeam' });
    const { state } = useLocalState();
    const studyId = (state?.selectedStudy?.id as string) ?? null;

    const { data, loading } = useQuery(GET_TRIALOS_STUDY_TEAM_MEMBERS, {
        variables: { studyId },
        skip: !studyId,
    });

    const rows = useMemo<TrialOsStudyTeamRow[]>(
        () =>
            (data?.trialOSStudyTeamMembers ?? [])
                .filter((member) => member !== null)
                .map((member) => ({
                    id: member.id as string,
                    riskIQ: member.riskIQ ? t(riskIQAccessTranslationKeys[member.riskIQ]) : '',
                    lastName: member.lastName ?? '',
                    firstName: member.firstName ?? '',
                    email: member.email ?? '',
                    createdOn: (member.created as string) ?? null,
                    createdBy: getFullName(member.createdBy),
                    updatedOn: (member.updated as string) ?? null,
                    updatedBy: getFullName(member.updatedBy),
                })),
        [data, t],
    );

    const columns = useMemo(() => getTrialOsStudyTeamColumns(t), [t]);

    return (
        <div className="k-h-full">
            <Grid
                title={t('grid.title')}
                testDataId="trialos-study-team-grid"
                data={rows}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                showAddIcon={false}
                maxHeight="auto"
                exportFilename={`${state?.selectedStudy?.studyNumber}_TrialOS Study Team_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.STUDY}
                dataType={AuditDataType.EXCEL_EXPORT}
                auditDescriptionPrefix="Study Admin - TrialOS Study Team"
                dataItemKey="id"
                scrollable="virtual"
            />
        </div>
    );
};

export default ManageTrialOsStudyTeam;
